import { useCallback, useEffect, useRef, useState } from 'react';
import { LocalStore } from 'shared/helpers/Store';

export const INSPECTION_SYNC_TOOLTIP_AUTO_SHOWN_MS = 10_000;

const STORAGE_PREFIX = 'ctms:inspectionSyncTooltipAutoShown:';

export function inspectionSyncTooltipAutoShownStorageKey(
  loadGuid: string,
): string {
  return `${STORAGE_PREFIX}${loadGuid}`;
}

function readHasAutoShown(loadGuid: string): boolean {
  return (
    LocalStore.get(inspectionSyncTooltipAutoShownStorageKey(loadGuid)) === '1'
  );
}

function persistAutoShown(loadGuid: string) {
  LocalStore.set(inspectionSyncTooltipAutoShownStorageKey(loadGuid), '1');
}

interface UseLoadInspectionSyncTooltipAutoShowArgs {
  loadGuid: string;
  isEligible: boolean;
  autoShowDurationMs?: number;
  enableAutoShow?: boolean;
}

export function useLoadInspectionSyncTooltipAutoShow({
  loadGuid,
  isEligible,
  autoShowDurationMs = INSPECTION_SYNC_TOOLTIP_AUTO_SHOWN_MS,
  enableAutoShow = false,
}: UseLoadInspectionSyncTooltipAutoShowArgs) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const autoCloseTimerRef = useRef<null | number>(null);

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current !== null) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, []);

  const persistAndClose = useCallback(() => {
    clearAutoCloseTimer();
    setIsTooltipOpen(false);
    persistAutoShown(loadGuid);
  }, [clearAutoCloseTimer, loadGuid]);

  useEffect(() => {
    if (!isEligible || !loadGuid || !enableAutoShow) {
      clearAutoCloseTimer();
      setIsTooltipOpen(false);
      return;
    }

    if (readHasAutoShown(loadGuid)) {
      return;
    }

    setIsTooltipOpen(true);
    autoCloseTimerRef.current = window.setTimeout(() => {
      autoCloseTimerRef.current = null;
      setIsTooltipOpen(false);
      persistAutoShown(loadGuid);
    }, autoShowDurationMs);

    return () => {
      clearAutoCloseTimer();
    };
  }, [
    autoShowDurationMs,
    clearAutoCloseTimer,
    enableAutoShow,
    isEligible,
    loadGuid,
  ]);

  const onTriggerMouseEnter = useCallback(() => {
    if (!isEligible) return;
    clearAutoCloseTimer();
    persistAutoShown(loadGuid);
    setIsTooltipOpen(true);
  }, [clearAutoCloseTimer, isEligible, loadGuid]);

  const onTriggerMouseLeave = useCallback(() => {
    if (!isEligible) return;
    if (readHasAutoShown(loadGuid)) {
      setIsTooltipOpen(false);
    }
  }, [isEligible, loadGuid]);

  const onTooltipClose = useCallback(() => {
    persistAndClose();
  }, [persistAndClose]);

  return {
    isTooltipOpen,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    onTooltipClose,
  };
}
